import { Accept } from "react-dropzone";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { UploadableModality } from "@/constants/dropzone";

import { Category, FilepickerFile } from "./types";

interface InitialState {
  selectedFiles: FilepickerFile[];
  pendingVideoFile: File | null;
  category?: Category;
  maxFiles?: number;
  modality?: UploadableModality;
  acceptedFileTypes?: Accept;
  isUploading: boolean;
  isSelectionConfirmed: boolean;
  currentInstanceId?: string;
}

const createInitialState = (): InitialState => ({
  selectedFiles: [],
  pendingVideoFile: null,
  category: undefined,
  maxFiles: undefined,
  modality: undefined,
  acceptedFileTypes: undefined,
  isUploading: false,
  isSelectionConfirmed: false,
  currentInstanceId: undefined,
});

interface FilePickerState extends InitialState {
  setCategoryTab: (category: Category) => void;
  initCategoryTab: (category: Category) => void;
  setSelectedFile: (file: FilepickerFile) => void;
  setSelectedFiles: (files: FilepickerFile[]) => void;
  setPendingVideoFile: (file: File) => void;
  setMaxFiles: (maxFiles?: number) => void;
  setModality: (modality?: UploadableModality) => void;
  setAcceptedFileTypes: (acceptedFileTypes: Accept) => void;
  setIsUploading: (isUploading: boolean) => void;
  setSelectionAsConfirmed: () => void;
  setSelectionAsUnconfirmed: () => void;
  setInstanceId: (instanceId: string) => void;
  resetState: () => void;
}

export const useFilePickerState = create<FilePickerState>()(
  subscribeWithSelector((set) => ({
    ...createInitialState(),

    setCategoryTab: (category: Category) => {
      set({ category, selectedFiles: [] });
    },

    // Initialize the active tab when the picker opens. Unlike `setCategoryTab`
    // (a user-driven tab switch, which clears the selection), this must NOT
    // clear `selectedFiles`: on fast machines an "upload from computer" started
    // right after opening can complete before the dialog's `onEntered`
    // transition fires, and clearing here would clobber that fresh selection —
    // leaving the dialog stuck open. It only sets the category.
    initCategoryTab: (category: Category) => {
      set({ category });
    },

    setMaxFiles: (maxFiles?: number) => {
      set({ maxFiles });
    },

    setModality: (modality?: UploadableModality) => {
      set({ modality });
    },

    setAcceptedFileTypes: (acceptedFileTypes: Accept) => {
      set({ acceptedFileTypes });
    },

    setIsUploading: (isUploading: boolean) => {
      set({ isUploading });
    },

    setSelectionAsConfirmed: () => {
      set({ isSelectionConfirmed: true });
    },

    setSelectionAsUnconfirmed: () => {
      set({ isSelectionConfirmed: false });
    },

    setInstanceId: (instanceId: string) => {
      set({ currentInstanceId: instanceId });
    },

    setSelectedFile: (file: FilepickerFile) => {
      set({ selectedFiles: [file] });
    },

    setSelectedFiles: (files: FilepickerFile[]) => {
      set((state) => {
        if (typeof state.maxFiles !== "undefined") {
          return { selectedFiles: files.slice(-state.maxFiles) };
        }

        return { selectedFiles: files };
      });
    },

    setPendingVideoFile: (file: File) => {
      set({ pendingVideoFile: file });
    },

    resetState: () => {
      set(createInitialState());
    },
  })),
);
